/**
 * NetSuite item mapping
 * ---------------------
 * Translates a Grobotic SERVICE_MENU product (as it appears on a deal) into the
 * NetSuite line item(s) + invoice line that should be entered in NetSuite.
 *
 * Source of truth: "Zero to NetSuite to Invoice Item Mapping" sheet.
 *
 * Pricing model (from the sheet's example — Meta Advertising 30% $100):
 *   - Ad-revenue products split into an Ad Spend (PL) line and a Management Fee
 *     line. Management Fee = price × margin%, Ad Spend = price − Management Fee.
 *     (For Meta $100 @ 30%: $70 spend / $30 fee.) The single invoice line is the
 *     full price ($100).
 *   - Retainer / one-time / single-line products map to one NetSuite item at the
 *     full price; the invoice line is also the full price.
 *
 * Matching is by product name (case-insensitive, whitespace-collapsed). A few
 * SERVICE_MENU names differ from the sheet's labels — those are bridged in
 * ALIASES. Products with no NetSuite equivalent are marked `na: true`; products
 * not found here at all are reported as unmapped by the consumer.
 *
 * NOTE: two invoice names below are preserved verbatim from the source sheet and
 * look like typos — "Paid Search Advertising" and "Google Perfomance Management
 * Fee". They are kept as-is so they match NetSuite exactly; confirm with finance.
 */

export type PricePortion = 'spend' | 'fee' | 'full'

export interface NetSuiteLineTemplate {
  name: string
  /** Which slice of the product price this NetSuite item represents. */
  portion: PricePortion
}

export interface NetSuiteMapping {
  netsuiteItems: NetSuiteLineTemplate[]
  invoiceName: string
  /** True when the product is known to have no NetSuite equivalent. */
  na?: boolean
  /** True when the product bills ONCE rather than monthly — setup fees,
   *  expedited launches, and discrete creative projects. Consumers must push
   *  these with frequency='One Time' / is_mrr=false, otherwise the fee counts
   *  as recurring revenue and inflates MRR for the whole contract term.
   *  Declared here because the mapping is the business's own statement of
   *  what a product IS; deriving it from a hand-maintained name list meant
   *  every setup fee added after the list was written counted as recurring. */
  oneTime?: boolean
}

/** Minimal product shape needed to resolve + price a mapping. */
export interface NetSuiteProductInput {
  productName?: string
  description?: string
  price?: number | string
  margin?: number | string
  qty?: number | string
}

type MappingEntry = NetSuiteMapping | ((product: NetSuiteProductInput) => NetSuiteMapping)

// ── Reusable templates ───────────────────────────────────────────────────────

const DISPLAY: NetSuiteMapping = {
  netsuiteItems: [
    { name: 'Display Ad Spend (PL)', portion: 'spend' },
    { name: 'Display Management Fee', portion: 'fee' },
  ],
  invoiceName: 'Display Advertising',
}

const PROGRAMMATIC: NetSuiteMapping = {
  netsuiteItems: [
    { name: 'Programmatic Ad Spend (PL)', portion: 'spend' },
    { name: 'Programmatic Management Fee', portion: 'fee' },
  ],
  invoiceName: 'Programmatic Advertising',
}

const PAID_SOCIAL: NetSuiteMapping = {
  netsuiteItems: [
    { name: 'Paid Social Ad Spend (PL)', portion: 'spend' },
    { name: 'Paid Social Management Fee', portion: 'fee' },
  ],
  invoiceName: 'Paid Social Advertising',
}

const PAID_SEARCH: NetSuiteMapping = {
  netsuiteItems: [
    { name: 'Paid Search Ad Spend (PL)', portion: 'spend' },
    { name: 'Paid Search Management Fee', portion: 'fee' },
  ],
  invoiceName: 'Paid Search Advertising',
}

const GOOGLE_PERFORMANCE_MAX: NetSuiteMapping = {
  netsuiteItems: [
    { name: 'Ad Spend - Google Performance Max', portion: 'spend' },
    { name: 'Google Perfomance Management Fee', portion: 'fee' }, // verbatim (likely typo)
  ],
  invoiceName: 'Google Performance Max Advertising',
}

const SEO_RETAINER: NetSuiteMapping = {
  netsuiteItems: [{ name: 'Search Engine Optimization Retainer', portion: 'full' }],
  invoiceName: 'Search Engine Optimization Retainer',
}

const CREATIVE_RETAINER: NetSuiteMapping = {
  netsuiteItems: [{ name: 'Creative Service Retainer', portion: 'full' }],
  invoiceName: 'Creative Service Retainer',
}

const CREATIVE_PROJECT_GENERIC: NetSuiteMapping = {
  netsuiteItems: [{ name: 'Creative project', portion: 'full' }],
  invoiceName: 'Creative project',
  // A discrete project (a logo, a photo shoot, a branding guide), not a
  // monthly retainer. Recurring creative work routes to CREATIVE_RETAINER.
  oneTime: true,
}

const WEBSITE_DESIGN: NetSuiteMapping = {
  netsuiteItems: [{ name: 'Website Design Services - P1/4', portion: 'full' }],
  invoiceName: 'Website Design Services',
}

const WEBSITE_LICENSE: NetSuiteMapping = {
  netsuiteItems: [{ name: 'Website License', portion: 'full' }],
  invoiceName: 'Website License',
}

const WEBSITE_HOSTING: NetSuiteMapping = {
  netsuiteItems: [{ name: 'Website Hosting Services', portion: 'full' }],
  invoiceName: 'Website Hosting Services',
}

const AGENCY_RETAINER: NetSuiteMapping = {
  netsuiteItems: [{ name: 'Agency Service Retainer', portion: 'full' }],
  invoiceName: 'Agency Service Retainer',
}

const IMPLEMENTATION: NetSuiteMapping = {
  netsuiteItems: [{ name: 'Various setup items', portion: 'full' }],
  invoiceName: 'Various setup items',
  // Setup / implementation / expedited-launch fees all bill once at kickoff.
  oneTime: true,
}

const CALL_TRACKING: NetSuiteMapping = {
  netsuiteItems: [{ name: 'Call Tracking - Pkg 1', portion: 'full' }],
  invoiceName: 'Call Tracking',
}

const LOOP_CORE: NetSuiteMapping = {
  netsuiteItems: [{ name: 'LOOP Analytics Core Software Subscription', portion: 'full' }],
  invoiceName: 'LOOP Analytics Core Software Subscription',
}

const LOOP_SETUP: NetSuiteMapping = {
  netsuiteItems: [{ name: 'LOOP Analytics Software Setup Fee', portion: 'full' }],
  invoiceName: 'LOOP Analytics Software Setup Fee',
  // One-off software setup. The recurring subscription is LOOP_CORE.
  oneTime: true,
}

const NA: NetSuiteMapping = { netsuiteItems: [], invoiceName: '', na: true }

// ── Conditional resolvers ────────────────────────────────────────────────────

/** Sheet rule: route to Programmatic when the description mentions programmatic/geofencing. */
const isProgrammatic = (description?: string): boolean => {
  const d = (description ?? '').toLowerCase()
  return d.includes('programmatic') || d.includes('geofence') || d.includes('geofencing')
}

const displayOrProgrammatic = (product: NetSuiteProductInput): NetSuiteMapping =>
  isProgrammatic(product.description) ? PROGRAMMATIC : DISPLAY

// ── Mapping table (keyed by normalized product name) ─────────────────────────

const MAPPING: Record<string, MappingEntry> = {
  'creative design': CREATIVE_RETAINER,
  'graphic design': CREATIVE_RETAINER, // recurring default; one-time variant → Creative Service Project
  'display advertising': displayOrProgrammatic,
  'google display advertising': DISPLAY,
  'display - geofence advertising': PROGRAMMATIC,
  'google demand gen': DISPLAY,
  'display/geofence management': displayOrProgrammatic,
  'google performance max': GOOGLE_PERFORMANCE_MAX,
  'paid search advertising': PAID_SEARCH,
  'paid search management': PAID_SEARCH,
  // Standalone management/fee line items roll up to their parent invoice line,
  // same as Paid Search Management Fee → Paid Search Advertising.
  'paid social management fee': PAID_SOCIAL,
  'display management fee': DISPLAY,
  // Per finance: enter Programmatic Display under the Display Advertising
  // invoice line (not Programmatic Advertising).
  'programmatic display advertising': DISPLAY,
  'meta advertising': PAID_SOCIAL,
  'linkedin advertising': PAID_SOCIAL,
  'snapchat advertising': PAID_SOCIAL,
  'social media advertising revenue': PAID_SOCIAL,
  'social media advertising management fee': PAID_SOCIAL,
  'tiktok revenue': PAID_SOCIAL,
  'tiktok advertising': PAID_SOCIAL,
  'spotify advertising': PAID_SOCIAL,
  youtube: PAID_SOCIAL,
  'youtube advertising': PAID_SOCIAL,
  'reddit advertising': PAID_SOCIAL,
  'social media management': PAID_SOCIAL,
  'social content calendar': PAID_SOCIAL,
  'social profile creation': PAID_SOCIAL,
  'microsoft advertising': PAID_SEARCH,
  'amazon advertising': PAID_SEARCH,
  'paid search - media spend': PAID_SEARCH,
  retainer: AGENCY_RETAINER,
  'agency retainer - digital': AGENCY_RETAINER,
  'agency retainer - website': AGENCY_RETAINER,
  'agency retainer - creative': CREATIVE_RETAINER,
  'creative services - hourly': CREATIVE_RETAINER,
  'graphic design - general': CREATIVE_RETAINER,
  'graphic design - revisions': CREATIVE_RETAINER,
  'graphic design - additional logo revisions': CREATIVE_RETAINER,
  'graphic design - logo creation': CREATIVE_PROJECT_GENERIC,
  'photo shoot': CREATIVE_PROJECT_GENERIC,
  'lifestyle video shoot': CREATIVE_PROJECT_GENERIC,
  'video shoot': CREATIVE_PROJECT_GENERIC,
  'drone shoot - add on': CREATIVE_PROJECT_GENERIC,
  'drone video - add on': CREATIVE_PROJECT_GENERIC,
  'hype video': CREATIVE_PROJECT_GENERIC,
  'video editing': CREATIVE_PROJECT_GENERIC,
  'photo editing': CREATIVE_PROJECT_GENERIC,
  'marketing collateral': CREATIVE_PROJECT_GENERIC,
  'design materials': CREATIVE_PROJECT_GENERIC,
  'business systems kit': CREATIVE_PROJECT_GENERIC,
  'branding guide': CREATIVE_PROJECT_GENERIC,
  'full brand build': CREATIVE_PROJECT_GENERIC,
  'property naming': CREATIVE_PROJECT_GENERIC,
  'seo - tier 1': SEO_RETAINER,
  'seo - tier 2': SEO_RETAINER,
  'seo tier 3': SEO_RETAINER,
  'seo - tier 3': SEO_RETAINER,
  'seo - tier 4': SEO_RETAINER,
  'seo - custom tier': SEO_RETAINER,
  'seo - additional location': SEO_RETAINER,
  'add-on seo': SEO_RETAINER,
  'add on - generative engine optimization': SEO_RETAINER,
  'listing sync pro': SEO_RETAINER,
  'competitor intelligence report': SEO_RETAINER,
  'website build': WEBSITE_DESIGN,
  'premium website build': WEBSITE_DESIGN,
  'e-commerce website build': WEBSITE_DESIGN,
  'e-commerce integration': WEBSITE_DESIGN,
  'custom api integration': WEBSITE_DESIGN,
  'content creation': WEBSITE_DESIGN,
  'content migration': WEBSITE_DESIGN,
  'website revisions': WEBSITE_DESIGN,
  'website revisions - additional': WEBSITE_DESIGN,
  'website - add on': WEBSITE_DESIGN,
  'website maintenance retainer': WEBSITE_DESIGN,
  'website build - page creation': WEBSITE_DESIGN,
  'website build - additional page creation': WEBSITE_DESIGN,
  'website hosting': WEBSITE_HOSTING,
  'website hosting + 60 minutes of maintenance': WEBSITE_HOSTING,
  'monthly hosting plan': WEBSITE_HOSTING,
  'server migration': WEBSITE_HOSTING,
  'website transfer fee': WEBSITE_HOSTING,
  'website license': WEBSITE_LICENSE,
  'website plan': WEBSITE_LICENSE,
  'monthly website plan': WEBSITE_LICENSE,
  'video services': CREATIVE_PROJECT_GENERIC,
  'photography services': CREATIVE_PROJECT_GENERIC,
  printing: NA,
  shipping: NA,
  // Pass-through cost items with no NetSuite revenue line.
  'talent/models': NA,
  'travel, lodging, misc.': NA,
  'projected budget': NA,
  'projected creative budget': NA,
  'gtm / ga4 analysis': NA,
  'gtm/ga4 implementation': NA,
  'gtm/ga4 repair': NA,
  // Conversion-tracking + reporting integration items have no dedicated
  // NetSuite invoice line — they're bundled into the parent service.
  'advanced conversion tracking/reporting': NA,
  'standard conversion tracking': NA,
  'ecommerce conversion tracking': NA,
  'lead to lease reporting': NA,
  'integration services - hourly': NA,
  'implementation fee': IMPLEMENTATION,
  'setup fee - paid media': IMPLEMENTATION,
  'setup fee - website/creative': IMPLEMENTATION,
  'paid search - setup fee': IMPLEMENTATION,
  'social ads - setup fee': IMPLEMENTATION,
  'programmatic - setup fee': IMPLEMENTATION,
  'seo - setup fee': IMPLEMENTATION,
  'expedited launch': IMPLEMENTATION,
  'expedited website launch': IMPLEMENTATION,
  'expedited creative launch': IMPLEMENTATION,
  // Programmatic-style management fees route to the Programmatic
  // template per the sheet's geofencing convention.
  'programmatic display management fee': PROGRAMMATIC,
  'geofencing advertising management fee': PROGRAMMATIC,
  'retargeting advertising management fee': PROGRAMMATIC,
  'audio management fee': PROGRAMMATIC,
  'video management fee': PROGRAMMATIC,
  // Google AI Max — currently a Google-Performance-Max-style buy until
  // finance confirms otherwise. Route through GOOGLE_PERFORMANCE_MAX.
  'google ai max': GOOGLE_PERFORMANCE_MAX,
  'landing page': WEBSITE_DESIGN,
  'call tracking': CALL_TRACKING,
  'call tracking implementation': CALL_TRACKING,
  'call tracking diagnosis and repair': CALL_TRACKING,
  'consulting': AGENCY_RETAINER,
  'management fee / retainer': AGENCY_RETAINER,
  'loop core': LOOP_CORE,
  'loop setup': LOOP_SETUP,
  // Standalone Programmatic Audio / Video ad-revenue products route to
  // Programmatic per the sheet's geofencing convention.
  'programmatic audio advertising': PROGRAMMATIC,
  'programmatic video advertising': PROGRAMMATIC,
}

/**
 * Bridges actual SERVICE_MENU product names that differ from the source sheet's
 * labels → the canonical mapping key. Extend as new mismatches surface.
 */
const ALIASES: Record<string, string> = {
  'standard website build': 'website build',
  'website hosting + 30 minutes of maintenance': 'website hosting',
  'paid search management fee': 'paid search management',
  'gtm/ga4 analysis': 'gtm / ga4 analysis',
  'seo tier 1': 'seo - tier 1',
  'seo tier 2': 'seo - tier 2',
  'add on seo': 'add-on seo',
}

// ── Public API ───────────────────────────────────────────────────────────────

const normalize = (value: string): string => value.toLowerCase().replace(/\s+/g, ' ').trim()

const toNumber = (value: number | string | undefined): number => {
  if (value == null || value === '') return 0
  const n = typeof value === 'number' ? value : parseFloat(value)
  return Number.isFinite(n) ? n : 0
}

const round2 = (n: number): number => Math.round(n * 100) / 100

export const resolveNetSuiteMapping = (product: NetSuiteProductInput): NetSuiteMapping | null => {
  const key = normalize(product.productName ?? '')
  if (!key) return null
  const entry = MAPPING[ALIASES[key] ?? key]
  if (!entry) return null
  return typeof entry === 'function' ? entry(product) : entry
}

export interface NetSuiteBreakdownLine {
  name: string
  amount: number
}

export interface NetSuiteBreakdown {
  /** Whether the product was found in the mapping at all. */
  matched: boolean
  /** Matched, but the product is known to have no NetSuite equivalent. */
  na: boolean
  /** Matched, and the product bills once rather than monthly. */
  oneTime: boolean
  price: number
  marginPct: number
  /** NetSuite item lines with their dollar amounts. */
  items: NetSuiteBreakdownLine[]
  /** The single NetSuite invoice line, or null when unmapped/NA. */
  invoice: NetSuiteBreakdownLine | null
}

/**
 * Resolve + price a product's NetSuite breakdown. Ad-revenue products split the
 * price into spend (price − fee) and fee (price × margin%); single-line products
 * carry the full price. The invoice line is always the full product price.
 */
export const computeNetSuiteBreakdown = (product: NetSuiteProductInput): NetSuiteBreakdown => {
  const price = toNumber(product.price)
  const marginPct = toNumber(product.margin)
  const mapping = resolveNetSuiteMapping(product)

  if (!mapping) return { matched: false, na: false, oneTime: false, price, marginPct, items: [], invoice: null }
  if (mapping.na) return { matched: true, na: true, oneTime: false, price, marginPct, items: [], invoice: null }

  const fee = round2((price * marginPct) / 100)
  const spend = round2(price - fee)

  const items: NetSuiteBreakdownLine[] = mapping.netsuiteItems.map(item => {
    const amount = item.portion === 'spend' ? spend : item.portion === 'fee' ? fee : price
    return { name: item.name, amount }
  })

  return {
    matched: true,
    na: false,
    oneTime: !!mapping.oneTime,
    price,
    marginPct,
    items,
    invoice: { name: mapping.invoiceName, amount: price },
  }
}
